import { CandlestickSeries, createChart, createSeriesMarkers } from 'lightweight-charts';
import { useEffect, useRef, useState } from 'react';

const ContextualChart = ({ trade, onClose }) => {
    const chartContainerRef = useRef();
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        // Create chart instance
        const chart = createChart(chartContainerRef.current, {
            layout: { 
                background: { color: '#1e293b' },
                textColor: '#94a3b8',
            },
            grid: {
                vertLines: { color: '#334155' },
                horzLines: { color: '#334155' },
            },
            crosshair: {
                mode: 1, // Normal crosshair
            },
            rightPriceScale: {
                borderColor: '#334155',
            },
            timeScale: {
                borderColor: '#334155',
                timeVisible: true,
                secondsVisible: false,
            },
            width: chartContainerRef.current.clientWidth,
            height: chartContainerRef.current.clientHeight,
        });

        const candlestickSeries = chart.addSeries(CandlestickSeries, {
            upColor: '#10b981',
            downColor: '#ef4444',
            borderVisible: false,
            wickUpColor: '#10b981',
            wickDownColor: '#ef4444'
        });

        // Generate mock data based on trade timeframe for demonstration
        // In production, this data should be fetched from your Broker API given the entry_time and exit_time.
        const generateData = () => {
             const data = [];
             const entryDate = new Date(trade.entryTime || trade.entry_time || Date.now());
             // Start charting 30 minutes before entry
             entryDate.setMinutes(entryDate.getMinutes() - 30);
             
             const isOption = trade.securityType === "OPTION" || trade.security_type === "OPTION";
             let basePrice = isOption && trade.strikePrice 
                 ? parseFloat(trade.strikePrice) 
                 : parseFloat(trade.entryPrice || 100);

             let currentPrice = basePrice * 0.99; // start slightly lower/higher
             const now = Date.now();
             
             for (let i = 0; i < 150; i++) { 
                 if (entryDate.getTime() > now + 60000) break; // Don't graph into the future
                 const open = currentPrice;
                 const close = open + (Math.random() - 0.5) * (basePrice * 0.005);
                 const high = Math.max(open, close) + Math.random() * (basePrice * 0.002);
                 const low = Math.min(open, close) - Math.random() * (basePrice * 0.002);
                 
                 data.push({
                     time: Math.floor(entryDate.getTime() / 1000),
                     open,
                     high,
                     low,
                     close
                 });
                 
                 currentPrice = close;
                 entryDate.setMinutes(entryDate.getMinutes() + 1); // 1 min interval
             }
             return data;
        };

        const historicalData = generateData();
        if (historicalData.length > 0) {
            candlestickSeries.setData(historicalData);
        }

        // Calculate timestamps to place markers
        const safeIdxEntry = Math.min(30, Math.max(0, historicalData.length - 1));
        const safeIdxExit = Math.min(90, Math.max(0, historicalData.length - 1));
        const entryTs = trade.entryTime ? Math.floor(new Date(trade.entryTime).getTime() / 1000) : historicalData[safeIdxEntry]?.time;
        const exitTs = trade.exitTime ? Math.floor(new Date(trade.exitTime).getTime() / 1000) : historicalData[safeIdxExit]?.time;

        const markers = [];
        
        // Buy Marker
        if (trade.side === "BUY" || trade.side === "LONG") {
            markers.push({
                time: entryTs,
                position: 'belowBar',
                color: '#10b981',
                shape: 'arrowUp',
                text: 'BUY',
            });
            if (trade.status === 'closed') {
                markers.push({
                    time: exitTs,
                    position: 'aboveBar',
                    color: '#ef4444',
                    shape: 'arrowDown',
                    text: 'SELL',
                });
            }
        } else {
            // Short trade
            markers.push({
                time: entryTs,
                position: 'aboveBar',
                color: '#ef4444',
                shape: 'arrowDown',
                text: 'SHORT',
            });
            if (trade.status === 'closed') {
                markers.push({
                    time: exitTs,
                    position: 'belowBar',
                    color: '#10b981',
                    shape: 'arrowUp',
                    text: 'COVER',
                });
            }
        }

        // Sort markers by time as required by lightweight-charts
        markers.sort((a,b) => a.time - b.time);
        
        // Due to mock data, ensure markers are exactly on timelines that exist in the mock dataset
        const validatedMarkers = markers.filter(m => historicalData.some(d => d.time === m.time));
        const seriesMarkers = createSeriesMarkers(candlestickSeries);
        if (validatedMarkers.length > 0) {
            seriesMarkers.setMarkers(validatedMarkers);
        } else {
           // Fallback to plotting on arbitrary mock candles for demonstration if exact timestamps miss
           const fallbackMarkers = markers.map((m, idx) => ({
                ...m,
                time: historicalData[idx === 0 ? safeIdxEntry : safeIdxExit]?.time || m.time
           }));
           seriesMarkers.setMarkers(fallbackMarkers);
        }

        setLoading(false);

        // Handle window resize
        const handleResize = () => {
            if (chartContainerRef.current) {
                chart.applyOptions({
                    width: chartContainerRef.current.clientWidth,
                    height: chartContainerRef.current.clientHeight
                });
            }
        };

        window.addEventListener('resize', handleResize);
        return () => {
            window.removeEventListener('resize', handleResize);
            chart.remove();
        };
    }, [trade]);

    return (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.8)", zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'}} onClick={onClose}>
            <div onClick={(e) => e.stopPropagation()} className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden shadow-2xl" style={{ width: '90%', height: '80%', display: 'flex', flexDirection: 'column' }}>
                <div className="flex justify-between items-center px-4 py-3 border-b border-slate-800 bg-slate-900/50 backdrop-blur-md">
                   <h3 className="text-slate-200 font-semibold">{trade.symbol} • Contextual Trade Review</h3>
                   <button onClick={onClose} className="text-slate-400 hover:text-white pb-1 text-2xl leading-none">&times;</button>
                </div>
                <div style={{ flex: 1, position: 'relative' }} ref={chartContainerRef}>
                    {loading && <div className="absolute inset-0 flex items-center justify-center text-slate-400">Loading historical data...</div>}
                </div>
            </div>
        </div>
    );
};

export default ContextualChart;
